import { CacheLookupPolicy, InteractionRequiredAuthError, PublicClientApplication } from '@azure/msal-browser';

// Only the dashboard app registration: its token is good for the dashboard API and nothing else.
export const pca = new PublicClientApplication({
  auth: {
    clientId: import.meta.env.VITE_AZURE_CLIENT_ID,
    authority: `${import.meta.env.VITE_AZURE_AUTHORITY}/${import.meta.env.VITE_AZURE_TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: { cacheLocation: 'localStorage' },
});

const tokenRequest = { scopes: [import.meta.env.VITE_API_SCOPE] };
const overviewUrl = `${import.meta.env.VITE_API_BASE}/overview`;

// Signed in, but not in the viewer group. Or in a frame, outside the IP whitelist.
export class NoAccessError extends Error {}

// MSAL cannot redirect inside a frame, so a framed page uses the IP path only.
export const framed = window.self !== window.top;

// A redirect never returns: the page reloads after sign-in.
const redirecting = () => new Promise<never>(() => {});

async function accessToken() {
  const account = pca.getActiveAccount() ?? pca.getAllAccounts()[0];
  if (!account) {
    await pca.loginRedirect(tokenRequest);
    return redirecting();
  }
  try {
    // No hidden-iframe renewal: Entra cannot finish it in a sandboxed third-party frame. An expired refresh
    // token (24 h) goes straight to the redirect, which returns without a prompt while the Entra session lives.
    return (await pca.acquireTokenSilent({ ...tokenRequest, account, cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken })).accessToken;
  }
  catch (e) {
    if (!(e instanceof InteractionRequiredAuthError))
      throw e;
    await pca.acquireTokenRedirect(tokenRequest);
    return redirecting();
  }
}

// Always without a token first, so a whitelisted IP never needs a sign-in, even in a browser that signed in before.
export async function fetchOverview(): Promise<unknown> {
  const anonymous = await fetch(overviewUrl);
  if (anonymous.ok)
    return anonymous.json();
  if (anonymous.status !== 401 && anonymous.status !== 403)
    throw new Error(`Dashboard API answered ${anonymous.status}`);
  if (framed)
    throw new NoAccessError();

  const response = await fetch(overviewUrl, { headers: { Authorization: `Bearer ${await accessToken()}` } });
  if (response.status === 401 || response.status === 403)
    throw new NoAccessError();
  if (!response.ok)
    throw new Error(`Dashboard API answered ${response.status}`);
  return response.json();
}
